// DirectReceiveModal — confirm/edit one "Direct Import" line (multi-bin capable). Produces a staged
// DraftLine; committing to inventory happens later from the Import/Scan page ("Add to Stock").
import { useEffect, useState } from 'react';
import { Icon } from '@/foundation/icons';
import { Button, Chip, Field, Input, Modal, Select } from '@/foundation/primitives';
import { useActiveStoreId } from '@/api/useStores';
import type { Bin } from '@/domain/types';
import { fetchBatchDefault, fetchProductContext, type ProductContextResponse } from './api';
import { ProductBatchPanel } from './ProductBatchPanel';

export interface BinAlloc {
  bin_code: string;
  packs: number;
}

export interface DraftLine {
  id: string;
  sku: string;
  product_name: string;
  product_snapshot_id: string | null;
  gtin: string;
  batch_number: string;
  expiry_date: string;
  /** True when expiry_date is the auto-filled store default (no real expiry captured) — flags the
   *  received batch as "estimated" so the "Estimated expiry" label shows for Head-Office reporting. */
  expiry_is_default?: boolean;
  qty_packs: number;
  units_per_pack: number;
  bin_allocations: BinAlloc[];
  controlled: boolean;
  is_fridge: boolean;
  scanned_at: string;
  status: 'draft' | 'committing' | 'committed' | 'error';
  units_added?: number;
  error?: string;
}

const warnRed: React.CSSProperties = {
  fontSize: 'var(--fs-body-sm)',
  color: 'var(--error-dark)',
  background: 'var(--error-soft)',
  padding: '8px 10px',
  borderRadius: 'var(--radius-md)',
};
const warnYellow: React.CSSProperties = {
  fontSize: 'var(--fs-body-sm)',
  color: 'var(--warning-dark)',
  background: 'var(--warning-soft)',
  padding: '8px 10px',
  borderRadius: 'var(--radius-md)',
};

interface Props {
  initial: DraftLine;
  bins: Bin[];
  /** The store this receipt writes to — bins are filtered to it (the raw useBins
   *  list spans every store in the user's scope). */
  storeId?: string;
  onClose: () => void;
  onSave: (line: DraftLine) => void;
  /** Optional context banner rendered above the form (e.g. "N patients owing"). */
  notice?: React.ReactNode;
}

export function DirectReceiveModal({
  initial,
  bins,
  storeId: storeIdProp,
  onClose,
  onSave,
  notice,
}: Props) {
  const [qtyPacks, setQtyPacks] = useState(String(initial.qty_packs || 1));
  const [unitsPerPack, setUnitsPerPack] = useState(
    initial.units_per_pack ? String(initial.units_per_pack) : '',
  );
  const [batch, setBatch] = useState(initial.batch_number);
  const [expiry, setExpiry] = useState(initial.expiry_date);
  // The flow's explicit store wins; the global topbar store is only a fallback for
  // legacy callers. Everything below (bins, batch defaults, product context) keys
  // off the SAME store the receipt will write to.
  const activeStoreId = useActiveStoreId();
  const storeId = storeIdProp ?? activeStoreId;
  const scopedBins = storeId ? bins.filter((b) => b.store === storeId) : bins;
  // Pre-fill a DEFAULT batch/expiry when the scan/pick carried none (initial stock loading or a
  // plain non-GS1 barcode) so the operator always sees one, editable. A real GS1 batch/expiry that
  // `initial` already carries is kept (we only fill the empties).
  const [autofilled, setAutofilled] = useState(false);
  // True while the expiry field still holds the auto-filled store default (no real expiry captured).
  // Cleared the moment the operator edits the date. Sent as expiry_is_default so a batch received on
  // the default is flagged "estimated" for Head-Office reporting, instead of looking like a real date.
  const [expiryIsDefault, setExpiryIsDefault] = useState(false);
  useEffect(() => {
    if (!storeId || (initial.batch_number.trim() && initial.expiry_date)) return;
    fetchBatchDefault(storeId)
      .then((d) => {
        setBatch((b) => (b.trim() ? b : d.batch_number));
        setExpiry((e) => (e ? e : d.expiry_date));
        if (!initial.expiry_date) setExpiryIsDefault(true);
        setAutofilled(true);
      })
      .catch(() => {});
    // Run once when the store id resolves; only fills empty fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const [allocs, setAllocs] = useState<{ bin_code: string; packs: string }[]>(
    initial.bin_allocations.length
      ? initial.bin_allocations.map((a) => ({ bin_code: a.bin_code, packs: String(a.packs) }))
      : [{ bin_code: '', packs: '' }],
  );
  const [error, setError] = useState('');

  // Product context: current batches + a suggested bin (from previous records). Auto-select the
  // suggested bin when none is chosen yet, and surface the batches so the operator is stock-aware.
  const [ctx, setCtx] = useState<ProductContextResponse | null>(null);
  useEffect(() => {
    if (!storeId || (!initial.sku && !initial.product_snapshot_id)) return;
    fetchProductContext(storeId, {
      sku: initial.sku,
      snapshotId: initial.product_snapshot_id ?? undefined,
    })
      .then((c) => {
        setCtx(c);
        if (c.suggested_bin) {
          setAllocs((prev) =>
            prev.length === 1 && !prev[0].bin_code
              ? [{ ...prev[0], bin_code: c.suggested_bin }]
              : prev,
          );
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, initial.sku]);

  const qtyN = Number(qtyPacks);
  const uppN = Number(unitsPerPack);
  const totalUnits = (Number.isFinite(qtyN) ? qtyN : 0) * (Number.isFinite(uppN) ? uppN : 0);
  const multi = allocs.length > 1;
  const allocSum = allocs.reduce((s, a) => s + (Number(a.packs) || 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const expired = Boolean(expiry) && expiry < today;
  const shortExpiry =
    Boolean(expiry) && !expired && new Date(expiry).getTime() - Date.now() < 90 * 864e5;

  const binOptions = [
    { value: '', label: 'Select a bin…' },
    ...scopedBins.map((b) => ({ value: b.code, label: `${b.code} — ${b.label}` })),
  ];
  const setAlloc = (i: number, patch: Partial<{ bin_code: string; packs: string }>) =>
    setAllocs((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));

  // Bin capacity awareness. Capacity is a SOFT limit, so we WARN (never block) when this receipt
  // would push a chosen bin near/over its configured capacity — mirrors the Locations page so the
  // operator can pick another bin before it spills over.
  const binByCode = new Map(scopedBins.map((b) => [b.code, b]));
  const binFill = (code: string): { occ: number; cap: number; pct: number } | null => {
    const bin = binByCode.get(code.trim());
    if (!bin || !bin.cap) return null;
    return {
      occ: bin.occupiedQty,
      cap: bin.cap,
      pct: Math.round((bin.occupiedQty / bin.cap) * 100),
    };
  };
  const capNotices = allocs
    .map((a) => {
      const bin = binByCode.get(a.bin_code.trim());
      if (!bin || !bin.cap) return null;
      const adding = multi ? (Number(a.packs) || 0) * uppN : totalUnits;
      if (adding <= 0) return null;
      const projected = bin.occupiedQty + adding;
      const pct = Math.round((projected / bin.cap) * 100);
      // Only flag when the receipt would genuinely OVER-fill (>100%). Warning at 90% "nearly full"
      // cried wolf on every add — capacity is a soft limit and never blocks the receipt anyway.
      if (pct <= 100) return null;
      return { code: bin.code, adding, projected, cap: bin.cap, pct, over: true };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  const save = () => {
    if (!batch.trim()) return setError('Batch number is required');
    if (!expiry) return setError('Expiry date is required');
    if (expired) return setError('Stock has already expired — cannot add');
    if (!Number.isInteger(qtyN) || qtyN <= 0)
      return setError('Quantity (packs) must be a whole number greater than 0');
    if (!Number.isInteger(uppN) || uppN <= 0)
      return setError('Units per pack must be a whole number greater than 0');
    // single bin → all packs go to it; multi → packs must be entered and total the quantity
    const cleanAllocs: BinAlloc[] = multi
      ? allocs.map((a) => ({ bin_code: a.bin_code.trim(), packs: Number(a.packs) }))
      : [{ bin_code: allocs[0].bin_code.trim(), packs: qtyN }];
    if (cleanAllocs.some((a) => !a.bin_code)) return setError('Every allocation needs a bin');
    if (cleanAllocs.some((a) => !Number.isInteger(a.packs) || a.packs <= 0))
      return setError('Every bin needs a positive whole pack count');
    if (multi && allocSum !== qtyN)
      return setError(`Location allocations must total ${qtyN} packs (currently ${allocSum})`);
    setError('');
    onSave({
      ...initial,
      batch_number: batch.trim(),
      expiry_date: expiry,
      expiry_is_default: expiryIsDefault,
      qty_packs: qtyN,
      units_per_pack: uppN,
      bin_allocations: cleanAllocs,
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      width={560}
      title="Direct Import — Confirm line"
      subtitle="Confirm the details — this line is added straight to stock."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {notice}
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--grey-100)',
          }}
        >
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
            {initial.product_name || initial.sku}
          </div>
          <div
            style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)', marginTop: 2 }}
          >
            {initial.sku}
            {initial.gtin ? ` · GTIN ${initial.gtin}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Chip color={'violet' as never} size="s">
              Direct Import
            </Chip>
            {initial.controlled && (
              <Chip color="error" size="s">
                CD
              </Chip>
            )}
            {initial.is_fridge && (
              <Chip color={'primary' as never} size="s">
                Fridge
              </Chip>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <Field label="Quantity (packs)" required>
            <Input
              type="number"
              min={1}
              value={qtyPacks}
              onChange={(e) => setQtyPacks(e.target.value)}
            />
          </Field>
          <Field label="Units per pack" required>
            <Input
              type="number"
              min={1}
              value={unitsPerPack}
              onChange={(e) => setUnitsPerPack(e.target.value)}
            />
          </Field>
          <Field label="Batch number" required>
            <Input
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              placeholder="Batch / lot"
            />
          </Field>
          <Field label="Expiry date" required>
            <Input
              type="date"
              value={expiry}
              onChange={(e) => {
                setExpiry(e.target.value);
                setExpiryIsDefault(false); // operator entered a real date — no longer the default
              }}
            />
          </Field>
        </div>

        {autofilled && (
          <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
            Batch &amp; expiry were auto-filled with the store default (no batch on the scan) — edit
            if you have the real values.
          </div>
        )}
        {expired && <div style={warnRed}>⚠ This stock has already expired — cannot add.</div>}
        {shortExpiry && <div style={warnYellow}>⚠ Short expiry — under 3 months remaining.</div>}

        <Field label={multi ? 'Location allocations (packs per location)' : 'Location'} required>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {allocs.map((a, i) => {
              const fill = a.bin_code ? binFill(a.bin_code) : null;
              return (
                <div
                  key={i}
                  style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}
                >
                  <div style={{ flex: 1 }}>
                    <Select
                      value={a.bin_code}
                      onChange={(e) => setAlloc(i, { bin_code: e.target.value })}
                      options={binOptions}
                    />
                    {fill && (
                      <div
                        className="caption"
                        style={{
                          marginTop: 3,
                          color:
                            fill.pct >= 100
                              ? 'var(--error-dark)'
                              : fill.pct >= 90
                                ? 'var(--warning-dark)'
                                : 'var(--text-secondary)',
                        }}
                      >
                        Currently {fill.occ}/{fill.cap} ({fill.pct}%)
                        {fill.pct >= 100 ? ' · over capacity' : ''}
                      </div>
                    )}
                  </div>
                  {multi && (
                    <div style={{ width: 96 }}>
                      <Input
                        type="number"
                        min={1}
                        placeholder="packs"
                        value={a.packs}
                        onChange={(e) => setAlloc(i, { packs: e.target.value })}
                      />
                    </div>
                  )}
                  {multi && (
                    <Button
                      size="s"
                      variant="text"
                      title="Remove bin"
                      onClick={() => setAllocs((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Icon name="trash" size={14} />
                    </Button>
                  )}
                </div>
              );
            })}
            <div>
              <Button
                size="s"
                variant="text"
                onClick={() => setAllocs((prev) => [...prev, { bin_code: '', packs: '' }])}
              >
                + Add another bin
              </Button>
            </div>
            {multi && (
              <div
                className="caption"
                style={{ color: allocSum === qtyN ? 'var(--success-dark)' : 'var(--warning-dark)' }}
              >
                Allocated {allocSum} / {qtyN || 0} packs
              </div>
            )}
            {ctx?.suggested_bin && (
              <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
                <Icon name="locations" size={12} /> Suggested bin{' '}
                <strong style={{ color: 'var(--text-primary)' }}>{ctx.suggested_bin}</strong>
                {ctx.suggested_reason ? ` — ${ctx.suggested_reason}` : ''}
              </div>
            )}
          </div>
        </Field>

        {capNotices.map((n) => (
          <div key={n.code} style={warnYellow}>
            ⚠ Heads-up: adding {n.adding} unit(s) to <strong>{n.code}</strong> → {n.projected}/
            {n.cap} ({n.pct}%), over its configured capacity. Capacity is a soft guide — you can
            still add here, or pick another location.
          </div>
        ))}

        {ctx && ctx.batches.length > 0 && <ProductBatchPanel batches={ctx.batches} />}

        <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-secondary)' }}>
          {qtyN || 0} packs × {uppN || 0} units = <strong>{totalUnits} units</strong> to stock.
        </div>

        {error && <div style={warnRed}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="check" onClick={save} disabled={expired}>
            Save line
          </Button>
        </div>
      </div>
    </Modal>
  );
}
